import { Controller } from '@hotwired/stimulus';
import * as Turbo from '@hotwired/turbo';
// Extension `.js` explicite : esbuild s'en passe, `node --test` non.
import { isInteractiveTarget } from '../utils/interactive_target.js';

/**
 * Makes a table row keyboard-accessible and clickable for navigation or download.
 *
 * Automatically sets tabindex="0" and cursor-pointer on connect (only when a
 * url value is present — orphan rows without a URL are not decorated).
 * Enter key triggers the action (link semantics).
 * Guards against firing when clicking inner interactive elements
 * (links, buttons, inputs — shared guard `utils/interactive_target.js`, which
 * excludes the row itself so a `role="button"` row never self-blocks) and
 * modifier keys (Cmd/Ctrl for new-tab).
 *
 * Values:
 * - url (String): The URL to navigate to (visit) or download from (download)
 *
 * Actions:
 * - visit: Navigate via Turbo.visit() — for HTML pages
 * - visitKeyboard: Enter-key variant of visit
 * - download: Trigger a browser download via window.location.href — for binary
 *   attachment-disposition responses (Turbo.visit would parse them as HTML)
 * - downloadKeyboard: Enter-key variant of download
 *
 * Note: PDF rows do NOT use clickable-row — they use `pdf-preview#openFromRow`
 * to open the in-app preview. Non-PDF rows use `clickable-row#download` to
 * stream the file. The two mechanisms are mutually exclusive per row.
 *
 * @example
 * <tr data-controller="clickable-row"
 *     data-clickable-row-url-value="<%= item_path(item) %>"
 *     data-action="click->clickable-row#visit keydown->clickable-row#visitKeyboard"
 *     aria-label="Voir l'élément">
 *
 * @example
 * <tr data-controller="clickable-row"
 *     data-clickable-row-url-value="<%= download_document_path(doc) %>"
 *     data-action="click->clickable-row#download keydown->clickable-row#downloadKeyboard"
 *     aria-label="Télécharger le document">
 */
export default class extends Controller {
  static values = { url: String };

  connect() {
    if (!this.hasUrlValue) return;
    this.element.setAttribute('tabindex', '0');
    this.element.classList.add('cursor-pointer');
  }

  visit(event) {
    if (!this.hasUrlValue) return;
    if (isInteractiveTarget(event, this.element)) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey) return;

    Turbo.visit(this.urlValue);
  }

  visitKeyboard(event) {
    if (event.key !== 'Enter') return;
    if (!this.hasUrlValue) return;

    event.preventDefault();
    Turbo.visit(this.urlValue);
  }

  download(event) {
    if (!this.hasUrlValue) return;
    if (isInteractiveTarget(event, this.element)) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey) return;

    window.location.href = this.urlValue;
  }

  downloadKeyboard(event) {
    if (event.key !== 'Enter') return;
    if (!this.hasUrlValue) return;

    event.preventDefault();
    window.location.href = this.urlValue;
  }
}
